import "./App.css";

import React, { useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useParams,
  useLocation,
} from "react-router-dom";
import { Layout } from "antd";
import Home from "./pages/Home";
import Dashboard from "./pages/Dashboard";
import DreamCreate from "./pages/DreamCreate";
import MyDreams from "./pages/MyDreams";
import DreamDetail from "./pages/DreamDetail";
import Track from "./pages/Track";
import Profile from "./pages/Profile";
import Settings from "./pages/Settings";
import Navbar from "./components/Navbar";
import ProtectedRoute from "./components/ProtectedRoute";
import FooterBar from "./components/FooterBar";
import Container from "./components/Container";
import { useAuth0 } from "@auth0/auth0-react";
import { useAppDispatch, useAppSelector } from "./store/hooks";
import { fetchTrackFull } from "./store/dashboardSlice";
import { fetchSettings } from "./store/settingsSlice";
import { fetchProfileMeta } from "./store/profileSlice";
import { APP_ROUTES } from "./utils/appRoutes";
import { cn } from "./utils/cn";
import { openFeedback } from "./config/sentry";

const RedirectGoalToDream = () => {
  const { id } = useParams();
  if (!id) {
    return <Navigate to={APP_ROUTES.dashboard} replace />;
  }
  return <Navigate to={APP_ROUTES.dreamDetail(id)} replace />;
};

const AppContent = () => {
  const { pathname } = useLocation();
  const dispatch = useAppDispatch();
  const { isAuthenticated, isLoading } = useAppSelector((state) => state.auth);
  const { getAccessTokenSilently } = useAuth0();
  const isHomePage = pathname === APP_ROUTES.home;
  const showMinimalLayout = isHomePage && !isAuthenticated;

  useEffect(() => {
    if (!isAuthenticated) return;
    const getToken = () => getAccessTokenSilently({ cacheMode: "off" });
    dispatch(fetchTrackFull());
    dispatch(fetchSettings(getToken));
    dispatch(fetchProfileMeta(getToken));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, isAuthenticated]);

  const appRoutes = (
    <Routes>
      <Route path={APP_ROUTES.home} element={<Home />} />
      <Route
        path={APP_ROUTES.dashboard}
        element={<ProtectedRoute component={Dashboard} />}
      />
      <Route path={APP_ROUTES.plan} element={<ProtectedRoute component={Track} />} />
      <Route path={APP_ROUTES.planPattern} element={<ProtectedRoute component={Track} />} />
      <Route path={APP_ROUTES.myDreams} element={<ProtectedRoute component={MyDreams} />} />
      <Route
        path={APP_ROUTES.dreamCreate}
        element={<ProtectedRoute component={DreamCreate} />}
      />
      <Route
        path={APP_ROUTES.dreamDetailPattern}
        element={<ProtectedRoute component={DreamDetail} />}
      />
      <Route
        path={APP_ROUTES.goalDetailPattern}
        element={<ProtectedRoute component={RedirectGoalToDream} />}
      />
      <Route
        path={APP_ROUTES.settings}
        element={<ProtectedRoute component={Settings} />}
      />
      <Route
        path={APP_ROUTES.profile}
        element={<ProtectedRoute component={Profile} />}
      />
    </Routes>
  );

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white">
        <div className="flex flex-col items-center gap-4">
          <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
            <circle cx="24" cy="24" r="20" stroke="#E2E8F0" strokeWidth="4"/>
            <path d="M24 4a20 20 0 0 1 20 20" stroke="#0088FF" strokeWidth="4" strokeLinecap="round">
              <animateTransform attributeName="transform" type="rotate" from="0 24 24" to="360 24 24" dur="0.8s" repeatCount="indefinite"/>
            </path>
          </svg>
        </div>
      </div>
    );
  }

  return (
    <Layout className="min-h-screen" data-testid="app-shell">
      {!showMinimalLayout && <Navbar />}
      <Layout.Content
        className={cn(
          "flex-1",
          showMinimalLayout ? "" : "bg-surface-page px-5 pt-2 pb-4 md:px-6 md:py-6",
        )}
      >
        {showMinimalLayout ? appRoutes : <Container>{appRoutes}</Container>}
      </Layout.Content>
      {!showMinimalLayout && <FooterBar />}
      {isAuthenticated && !showMinimalLayout && (
        <button
          type="button"
          onClick={() => openFeedback()}
          style={{
            position: "fixed",
            bottom: 16,
            left: 16,
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "10px 16px",
            background: "#ffffff",
            border: "1px solid rgba(0,0,0,0.10)",
            borderRadius: 20,
            boxShadow: "0 2px 12px rgba(0,0,0,0.12)",
            cursor: "pointer",
            fontSize: 13,
            fontWeight: 600,
            color: "#1F1F1F",
            fontFamily: "inherit",
          }}
          aria-label="Open feedback form"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
          Give Feedback
        </button>
      )}
    </Layout>
  );
};

const App = () => {
  return (
    <Router>
      <AppContent />
    </Router>
  );
};

export default App;
